import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { rememberRoute } from '@/lib/lastRoute'
import { useRealtime } from '@/hooks/useRealtime'
import {
  BarChart3,
  BookOpen,
  Building2,
  ChevronsUpDown,
  ClipboardCheck,
  ClipboardList,
  Home,
  ListChecks,
  LogOut,
  Megaphone,
  QrCode,
  Settings,
  UserRound,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/utils'
import {
  MODULE_DISABLED_EVENT,
  moduleEnabled,
  moduleForPath,
  type ModuleDisabledDetail,
} from '@/lib/modules'
import { TWO_FACTOR_REQUIRED_EVENT } from '@/lib/api'
import type { ModuleName, User } from '@/types/api'
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { OfflineQueueBar } from './OfflineQueueBar'
import { UpdatePrompt } from './UpdatePrompt'
import { fullVersion, shortVersion } from '@/lib/version'
import { CompanyBrand } from './CompanyBrand'
import { ImpersonationBanner } from './ImpersonationBanner'
import { NotificationBell } from './NotificationBell'
import { ErrorBoundary } from './ErrorBoundary'
import { ModuleDisabled } from './ModuleDisabled'
import { ThemeToggle } from './ThemeProvider'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useT } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'
import { KaproLogo, KaproMark } from './Logo'

/*
 * Two navigations on purpose.
 *
 * Desktop gets the shadcn sidebar. Mobile keeps a bottom bar, because the
 * primary user is holding a phone one-handed next to a machine and the
 * reachable part of that screen is the bottom third — a top drawer would put
 * every destination out of thumb reach.
 */

interface NavItem {
  to: string
  /**
   * Translation key, not a label.
   *
   * `NAV` is a module-level constant, so there is no hook available where it is
   * declared — the component resolves these at render instead. That is also
   * what makes the sidebar relabel itself on a language switch rather than
   * keeping whatever language it was first mounted in.
   */
  labelKey: TranslationKey
  icon: typeof Home
  /**
   * What the destination is for, shown as the bottom bar's tooltip.
   *
   * Not a repeat of `label` — the label is already printed under the icon
   * there, so echoing it would be noise. The collapsed sidebar is the opposite
   * case: the label *is* the missing information, so its tooltip uses `label`.
   */
  hintKey: TranslationKey
  managerOnly?: boolean
  adminOnly?: boolean
  /** Platform owner only — the one destination that crosses customers. */
  ownerOnly?: boolean
  /** Kept out of the mobile bottom bar, which only has room for four. */
  desktopOnly?: boolean
  /** Hidden when the company has this module switched off. */
  module?: ModuleName
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.home', icon: Home, hintKey: 'nav.home.hint' },
  {
    to: '/prijave',
    labelKey: 'nav.submissions',
    icon: ListChecks,
    hintKey: 'nav.submissions.hint',
    module: 'submissions',
  },
  {
    to: '/zadaci',
    labelKey: 'nav.tasks',
    icon: ClipboardList,
    hintKey: 'nav.tasks.hint',
    module: 'submissions',
  },
  { to: '/5s', labelKey: 'nav.fiveS', icon: ClipboardCheck, hintKey: 'nav.fiveS.hint', module: 'fiveS' },
  {
    to: '/analitika',
    labelKey: 'nav.analytics',
    icon: BarChart3,
    hintKey: 'nav.analytics.hint',
    managerOnly: true,
    desktopOnly: true,
    module: 'analytics',
  },
  {
    to: '/admin',
    labelKey: 'nav.admin',
    icon: Settings,
    hintKey: 'nav.admin.hint',
    adminOnly: true,
    desktopOnly: true,
  },
  {
    /*
     * Last in the list, and desktop-only.
     *
     * Nobody navigates here during a shift — it is read once when an account is
     * new and then when something refuses to work. The bottom bar has room for
     * four and this is not one of the four; the guide reaches a phone through
     * the link in the empty state or the header, not through a permanent slot.
     */
    to: '/uputstvo',
    labelKey: 'nav.guide',
    icon: BookOpen,
    hintKey: 'nav.guide.hint',
    desktopOnly: true,
  },
  {
    to: '/vlasnik',
    /*
     * The way back, not the way in.
     *
     * An owner lands in their own console and never normally sees this shell —
     * `HomeForRole` redirects them. This exists for the case where one deep
     * links into a customer screen and needs an exit that is not the browser's
     * back button.
     */
    labelKey: 'nav.owner',
    icon: Building2,
    hintKey: 'nav.owner.hint',
    ownerOnly: true,
    desktopOnly: true,
  },
]

/**
 * Whether the rail was left expanded last time.
 *
 * shadcn's `SidebarProvider` writes `sidebar_state` on every toggle but never
 * reads it back — in the Next.js template the cookie is read on the server and
 * handed in as `defaultOpen`. There is no server doing that in a Vite SPA, so
 * the write went nowhere and a collapsed rail sprang back open on every reload.
 * This is the missing half.
 */
function storedSidebarOpen(): boolean {
  const match = document.cookie.match(/(?:^|;\s*)sidebar_state=(true|false)/)

  return match ? match[1] === 'true' : true
}

/**
 * The account menu's contents, shared by both navigations.
 *
 * Extracted because it was only ever in the sidebar footer — which is
 * `hidden lg:flex` — so on a phone there was **no route to /profil at all**.
 * The page existed, was linked from the desktop rail, and was unreachable for
 * exactly the people who use the app most.
 *
 * Defining it once means the mobile trigger cannot drift from the desktop one
 * the next time an item is added here.
 */
function AccountMenuItems({
  user,
  onProfile,
  onLogout,
  extras = [],
  onNavigate,
}: {
  user: User | null
  onProfile: () => void
  onLogout: () => void
  /**
   * Destinations that have no other route on this screen.
   *
   * The bottom bar holds four tabs, so Analitika and Administracija are marked
   * `desktopOnly` and filtered out of it — which on a phone left a manager or
   * an administrator with **no way to reach them at all**. The sidebar carries
   * them on desktop; this menu is where they live on mobile.
   *
   * Empty for the sidebar's own copy of this menu, where listing them again
   * would duplicate the rail two inches above.
   */
  extras?: NavItem[]
  onNavigate?: (to: string) => void
}) {
  const t = useT()

  return (
    <>
      {/*
        The Group wrapper is required, not decorative.

        This shadcn build is on Base UI, where DropdownMenuLabel is
        `Menu.GroupLabel` and throws "MenuGroupContext is missing" unless it has
        a `Menu.Group` ancestor. On Radix — which most shadcn snippets assume —
        the label stands alone quite happily. Same component name, different
        contract.
      */}
      <DropdownMenuGroup>
        <DropdownMenuLabel className="p-2">
          <span className="text-foreground block truncate text-sm font-medium">
            {user?.name}
          </span>
          <span className="block truncate text-xs font-normal">
            {user?.employeeCode} · {user?.roleLabel}
          </span>
        </DropdownMenuLabel>
      </DropdownMenuGroup>

      <DropdownMenuSeparator />

      {extras.length > 0 && (
        <>
          {extras.map(({ to, labelKey, icon: Icon }) => (
            <DropdownMenuItem
              key={to}
              onClick={() => onNavigate?.(to)}
              className="min-h-10 px-2"
            >
              <Icon />
              {t(labelKey)}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />
        </>
      )}

      <DropdownMenuItem onClick={onProfile} className="min-h-10 px-2">
        <UserRound />
        {t('account.myAccount')}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem variant="destructive" onClick={onLogout} className="min-h-10 px-2">
        <LogOut />
        {t('account.signOut')}
      </DropdownMenuItem>
    </>
  )
}

function initials(name?: string): string {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export function AppShell() {
  const { user, impersonation, logout, refreshUser } = useAuth()
  const t = useT()
  const navigate = useNavigate()
  const location = useLocation()

  /*
   * A module switched off while this person was signed in.
   *
   * Their session still says it is on — the owner changed it a minute ago —
   * so the navigation offers it and the first request into it comes back 403
   * with the module named. The interceptor announces that; this is where it
   * lands. Two things happen: the session is re-read, so the navigation and
   * the route guards catch up, and if the refusal was about the page on
   * screen, the page is replaced by the explanation straight away instead of
   * by whatever error state it would have drawn.
   *
   * "About the page on screen" is the path check. Home reads submissions as
   * well, and a refused tile there must not replace the whole home screen.
   */
  const [refused, setRefused] = useState<{ module: ModuleName; path: string } | null>(null)
  const lastRefresh = useRef(0)

  useEffect(() => {
    function onRefused(event: Event) {
      const { module } = (event as CustomEvent<ModuleDisabledDetail>).detail

      setRefused({ module, path: window.location.pathname })

      /*
       * Once per burst. A dashboard fires three or four requests at once and
       * every one of them is refused; one re-read answers all of them.
       */
      if (Date.now() - lastRefresh.current > 10_000) {
        lastRefresh.current = Date.now()
        refreshUser().catch(() => {
          // The guard still shows the explanation; a failed re-read only
          // means the navigation catches up on the next sign-in instead.
        })
      }
    }

    window.addEventListener(MODULE_DISABLED_EVENT, onRefused)

    /*
     * Two-factor newly required: the same once-per-burst re-read, so the
     * session learns it and the Administracija gate takes over the screen.
     */
    const onTwoFactor = () => {
      if (Date.now() - lastRefresh.current > 10_000) {
        lastRefresh.current = Date.now()
        refreshUser().catch(() => {})
      }
    }

    window.addEventListener(TWO_FACTOR_REQUIRED_EVENT, onTwoFactor)

    return () => {
      window.removeEventListener(MODULE_DISABLED_EVENT, onRefused)
      window.removeEventListener(TWO_FACTOR_REQUIRED_EVENT, onTwoFactor)
    }
  }, [refreshUser])

  const refusedHere =
    refused &&
    refused.path === location.pathname &&
    moduleForPath(location.pathname) === refused.module
      ? refused.module
      : null

  const modules = user?.company?.modules

  // Recorded on every navigation so that reopening the app — or a session
  // expiring overnight — returns the user to the screen they were working on.
  useEffect(() => {
    rememberRoute(location.pathname + location.search)
  }, [location.pathname, location.search])

  /*
   * Live updates over Reverb, scoped to what this role may hear.
   *
   * Mounted here rather than per page: a supervisor's queue should update while
   * they are reading a submission, and the bell should light up wherever they
   * are. Does nothing if Reverb is not configured or not running — polling
   * still covers it.
   */
  useRealtime()

  const items = NAV.filter(
    (item) =>
      (!item.managerOnly || user?.isManager) &&
      (!item.adminOnly || user?.role === 'ADMIN') &&
      /*
       * Hidden during a support session as well as from customers.
       *
       * The session runs as the customer's user, so `isOwner` is already false
       * and this is belt-and-braces — but the server refuses the panel from
       * inside a support session for a reason worth repeating in the UI: the
       * cross-customer view has no business being on a screen the customer's
       * own administrator might be watching.
       */
      (!item.ownerOnly || (user?.isOwner && !impersonation)) &&
      // A module the company does not have is not offered at all — a
      // destination that only says "not switched on" is not a destination.
      (!item.module || moduleEnabled(modules, item.module)),
  )

  const mobileItems = items.filter((item) => !item.desktopOnly)

  /*
   * Which tab the glass pill sits over.
   *
   * -1 on a route with no tab of its own — /profil, /skeniraj, a submission
   * detail — and the pill fades out rather than parking on whichever tab
   * happens to be first. Claiming a tab the user is not on is worse than
   * showing none.
   */
  const activeIndex = mobileItems.findIndex(({ to }) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to),
  )

  async function handleLogout() {
    await logout()
    navigate('/prijava')
  }

  return (
    /*
     * TooltipProvider groups the rail's tooltips: once one has been shown,
     * moving to the next item shows it immediately instead of waiting out the
     * delay again. Without it each item is its own island and scanning a
     * collapsed rail means a pause per icon.
     */
    <TooltipProvider>
      <SidebarProvider
        defaultOpen={storedSidebarOpen()}
        // 4rem, not shadcn's 3rem. The collapsed rail has to fit a 44px target
        // plus the menu's own padding; at 3rem the icons are squeezed to 32px.
        style={{ '--sidebar-width-icon': '4rem' } as CSSProperties}
      >
        {/*
          Desktop navigation.

          The glass is applied to the inner panel through an arbitrary variant
          rather than by editing sidebar.tsx: `className` on <Sidebar> lands on
          the outer container, and the element that actually paints the
          background is `[data-slot=sidebar-inner]` one level down. Reaching it
          this way keeps the vendored shadcn file untouched, so re-running the
          CLI does not wipe it.
        */}
        <Sidebar
          collapsible="icon"
          className={cn(
            'hidden lg:flex',
            '[&_[data-slot=sidebar-inner]]:bg-sidebar/70',
            '[&_[data-slot=sidebar-inner]]:supports-[backdrop-filter]:bg-sidebar/55',
            '[&_[data-slot=sidebar-inner]]:backdrop-blur-2xl',
            '[&_[data-slot=sidebar-inner]]:backdrop-saturate-150',
          )}
        >
        <SidebarHeader>
          {/* justify-center only when collapsed. With `justify-between` and the
              full logo hidden, the lone mark was pushed to the left edge of the
              rail instead of sitting under the icons below it. */}
          <div className="flex items-center gap-2 px-2 py-1 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
            <KaproLogo className="group-data-[collapsible=icon]:hidden" showTagline />
            <KaproMark className="hidden size-9 shrink-0 group-data-[collapsible=icon]:block" />
          </div>

        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(({ to, labelKey, icon: Icon }) => (
                  <SidebarMenuItem key={to}>
                    {/*
                      `render`, not Radix's `asChild` — this shadcn build sits
                      on Base UI, which composes through a render prop.
                    */}
                    <SidebarMenuButton
                      render={<NavLink to={to} end={to === '/'} />}
                      isActive={
                        to === '/' ? location.pathname === '/' : location.pathname.startsWith(to)
                      }
                      // Shown only while the rail is collapsed — SidebarMenuButton
                      // hides it in the expanded state, where the label is right
                      // there next to the icon.
                      tooltip={t(labelKey)}
                      className={cn(
                        // shadcn's menu button is h-8. This is a desk-and-mouse
                        // surface, but the 44px rule is applied throughout so
                        // the two navigations feel like one product.
                        'h-11 gap-3 rounded-xl px-3',
                        // The collapsed sizing carries `!` in shadcn's own
                        // variants, so matching it here needs `!` too.
                        'group-data-[collapsible=icon]:size-11!',
                        /*
                         * Centre the icon when there is nothing beside it.
                         *
                         * The button keeps its `px-3` and `gap-3` in the
                         * collapsed rail, and the label is only clipped by
                         * `overflow-hidden` rather than removed — so the icon
                         * sat left of centre, offset by the padding and by a
                         * label that was still taking part in the layout. The
                         * label is hidden outright below.
                         */
                        'group-data-[collapsible=icon]:justify-center',
                        'group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0!',
                        /*
                         * And centre the button inside the rail.
                         *
                         * `justify-center` above centres the icon within the button; it
                         * says nothing about where the button sits. `SidebarGroup` carries
                         * `p-2`, so the 64px rail has a 48px content box, the button is
                         * forced to 44px, and `SidebarMenuItem` is only `relative` —
                         * nothing centres it. It sat flush left at 8px and came out
                         * centred on 30 against a rail centre of 32.
                         *
                         * Two pixels, and visible: the icons read as a column slightly
                         * left of the mark above them and the avatar below, each of which
                         * is centred by its own container.
                         */
                        'group-data-[collapsible=icon]:mx-auto',
                        // A solid pill for the current page rather than the
                        // default faint tint — on a rail of six near-identical
                        // grey icons, a tint is easy to miss at a glance.
                        'data-active:bg-primary data-active:text-primary-foreground',
                        'data-active:hover:bg-primary data-active:hover:text-primary-foreground',
                        '[&_svg]:size-5',
                      )}
                    >
                      <Icon />
                      <span className="group-data-[collapsible=icon]:hidden">{t(labelKey)}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          {/*
            Whose factory this is, below the navigation.

            Not in the sidebar header, where it sat directly under the product's
            own wordmark and the two marks read as one confused lockup. Down
            here it is clearly a separate statement — the software above, the
            company it is running for at the foot of the column, next to the
            person signed in.

            The mark alone, without the name beside it. Most logos *are* a
            wordmark — Livprom's spells the company out — so printing the name
            next to it said everything twice. It is still written in full in the
            page footer, which is where a legal name belongs.

            Hidden when the rail collapses: at 4rem there is no width for a
            wordmark, and a cropped logo is worse than none.
          */}
          <div className="border-[var(--border-subtle)] mx-2 mb-1 border-b pb-2 group-data-[collapsible=icon]:hidden">
            {/* Centred: it is a mark on its own now rather than the start of a
                line of text, and left-aligning it under a full-width rule left
                it looking dropped rather than placed. */}
            <CompanyBrand className="justify-center px-1" showName={false} />
          </div>

          <SidebarMenu>
            <SidebarMenuItem>
              {/*
                The account row opens a menu; it does not log you out.

                It used to *be* the logout button — avatar, name and role all
                inside it — so the only thing identifying you on screen was also
                the control that ended your session. Signing out is now a
                separate, explicitly destructive item one step away.

                No `tooltip` here on purpose: SidebarMenuButton implements it by
                wrapping itself in a TooltipTrigger's `render`, and this element
                is already a DropdownMenuTrigger's `render`. Two libraries
                composing onto one node is how you get a button that answers to
                neither. The menu shows the name anyway, which is what a tooltip
                would have said.
              */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      className={cn(
                        'h-12 gap-3 rounded-xl px-3',
                        'group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center',
                        'group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0!',
                        'group-data-[collapsible=icon]:mx-auto',
                      )}
                      aria-label={t('account.ariaLabel', { name: user?.name ?? '' })}
                    />
                  }
                >
                  <Avatar className="size-7 shrink-0">
                    <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs font-semibold">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  {/* Collapsed, the avatar alone stands for the account — the
                      name and the chevron would only push it off centre. */}
                  <span className="flex min-w-0 flex-1 flex-col text-left leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-sm font-medium">{user?.name}</span>
                    <span className="muted truncate text-xs">{user?.roleLabel}</span>
                  </span>
                  <ChevronsUpDown
                    className="size-4 shrink-0 opacity-60 group-data-[collapsible=icon]:hidden"
                    aria-hidden
                  />
                </DropdownMenuTrigger>

                {/* side=right: the rail is on the left edge, and a menu opening
                    upward would be clipped by the viewport when collapsed. */}
                <DropdownMenuContent
                  side="right"
                  align="end"
                  sideOffset={8}
                  className="supports-[backdrop-filter]:bg-popover/80 w-60 backdrop-blur-2xl backdrop-saturate-150"
                >
                  <AccountMenuItems
                    user={user}
                    onProfile={() => navigate('/profil')}
                    onLogout={handleLogout}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Transparent, so the body's ambient gradient reads through the whole
          content column instead of being covered by an opaque panel. */}
      <SidebarInset className="min-w-0 bg-transparent">
        {/* Connection and queue status. Renders nothing when online with an
            empty queue, so it costs no space in the normal case. */}
        <OfflineQueueBar />

        {/*
          One sticky stack, not two.

          The support banner has to stay on screen — that is the whole point of
          it — and so does the navigation bar. Two independently sticky elements
          both pinned to `top-0` would occupy the same strip, with the taller
          z-index simply covering the other. Sticking the *container* keeps them
          in document order and pins them as one block.

          When no support session is running the banner renders nothing and this
          behaves exactly as the bare sticky header did.
        */}
        <div className="sticky top-0 z-20">
          <ImpersonationBanner />

          {/*
            Translucent, like an iOS navigation bar — content scrolls *under* it
            rather than away from it, which is what makes the blur worth having.
            Opaque fallback first, since `backdrop-filter` on a transparent bar
            with no support gives unreadable text over content.
          */}
          <header
            className={cn(
              'flex items-center justify-between gap-2 px-4 py-2.5',
              'border-b border-black/5 dark:border-white/10',
              'bg-background/85 supports-[backdrop-filter]:bg-background/70',
              'backdrop-blur-xl backdrop-saturate-150',
            )}
          >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SidebarTrigger className="hidden lg:flex" />

            {/*
              The sidebar is desktop-only, so mobile keeps the wordmark here.

              `min-w-0` on every level down to the text, and `truncate` on both
              lines. Without it this block refuses to shrink below its content
              and the five controls on the right simply overlap it — which is
              what a phone showed: the notification bell sitting on top of the
              product name and a Bosnian surname running under it.
            */}
            <span className="flex min-w-0 items-center gap-2.5 lg:hidden">
              <KaproMark className="size-8 shrink-0" />
              <span className="min-w-0 leading-tight">
                {/*
                  The wordmark is the first thing to go on a narrow screen. It
                  is on the tab, the splash and the home-screen icon; the person
                  holding the phone knows what they opened. Whose account is
                  signed in is the fact that actually changes.
                */}
                <span className="hidden truncate font-semibold sm:block">KaProLean</span>
                <span className="muted block truncate text-xs sm:text-[0.6875rem]">
                  {user?.name}
                </span>
              </span>
            </span>

            {/*
              The customer's branding used to sit here, wedged between the rail
              toggle and the page. It lives in the sidebar header now — see
              there for why. On a phone, where there is no sidebar, the header
              already carries the product mark and the signed-in name, and a
              third element costs more than it tells anybody.
            */}
          </div>

          {/* `shrink-0`: the controls keep their width and the brand block
              above gives way, rather than both being squeezed into overlap. */}
          <div className="flex shrink-0 items-center gap-0.5">
            <NotificationBell />
            <LanguageSwitcher />
            <ThemeToggle />

            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/skeniraj')}
                    aria-label={t('header.scanQr')}
                    className="lg:hidden"
                  />
                }
              >
                <QrCode className="size-5" aria-hidden />
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('header.scanQr.hint')}</TooltipContent>
            </Tooltip>

            {/*
              The account menu, mobile edition.

              This slot used to be a bare logout button, which meant a phone had
              no way to reach /profil — the only link to it was in the sidebar
              footer, and the sidebar is `hidden lg:flex`. An avatar opening the
              same menu fixes that without adding a fifth icon to a header that
              is already tight: logout is still one tap away, just inside.
            */}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    className="hover:bg-muted focus-visible:ring-ring/50 flex size-11 shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:outline-none lg:hidden"
                    aria-label={`Nalog — ${user?.name ?? ''}`}
                  />
                }
              >
                <Avatar className="size-7">
                  <AvatarFallback className="bg-muted text-xs font-semibold">
                    {initials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>

              {/* Below the header, right-aligned under the avatar. */}
              <DropdownMenuContent
                side="bottom"
                align="end"
                sideOffset={8}
                className="supports-[backdrop-filter]:bg-popover/80 w-60 backdrop-blur-2xl backdrop-saturate-150"
              >
                <AccountMenuItems
                  user={user}
                  onProfile={() => navigate('/profil')}
                  onLogout={handleLogout}
                  /*
                   * The destinations the bottom bar has no room for. Without
                   * these a manager or administrator on a phone had no route to
                   * Analitika or Administracija anywhere in the application.
                   */
                  extras={items.filter((item) => item.desktopOnly)}
                  onNavigate={(to) => navigate(to)}
                />
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </header>
        </div>

        {/*
          A sunken canvas so the cards read as raised surfaces rather than as
          outlines drawn on the same sheet.

          The bottom padding clears the floating tab bar, the safe-area inset
          *and* the floating buttons, so the last row of a list is never trapped
          behind either.

          The buttons are `fixed`, so they take no space in the layout and sit
          over whatever happens to be at the bottom-right of the viewport when a
          page is scrolled to the end. That is almost always the pager — which is
          how this was found: `1 / 3` and the next-page arrow were underneath the
          megaphone, on a list of seventy-one people.

          The arithmetic, from the stack below: 56px for the report button, 12px
          of gap, 48px for the 5S one, on top of the offset the stack itself sits
          at. 88px + 116px ≈ 13rem below `lg`, 64px + 116px ≈ 12rem above it.
          Padded for both buttons even though the 5S one only renders for
          somebody who may evaluate — a reserve that depends on the reader's role
          is a layout bug waiting for the right account to open the page.

          `sm:pb-*` is spelled out because `sm:p-6` would otherwise reset the
          bottom padding at that breakpoint and leave tablets — which still use
          the mobile button offset — with 24px of clearance.
        */}
        <main className="min-w-0 flex-1 p-4 pb-[calc(13rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(13rem+env(safe-area-inset-bottom))] lg:pb-48">
          {/*
            Scoped to the routed page, not the whole app: a crash in one screen
            leaves the navigation and the notification bell working, and keying
            it on the path means navigating away clears it.
          */}
          <ErrorBoundary resetKey={location.pathname}>
            {/*
              Keyed on the path so each screen fades and lifts in as the tab
              bar's pill slides — the two motions read as one gesture.

              Keying costs nothing here: a route change unmounts the old page
              regardless, and a change of query string alone (?otvorene=true)
              leaves the key untouched, so filtering a list does not re-animate
              or lose its state.
            */}
            <div
              key={location.pathname}
              className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
            >
              {refusedHere ? <ModuleDisabled module={refusedHere} /> : <Outlet />}
            </div>
          </ErrorBoundary>
        </main>

        {/*
          Attribution, always on screen.

          `sticky bottom-0` rather than `fixed`: the page still scrolls
          normally, but the bar stays glued to the bottom of the viewport
          without ever needing to be scrolled to — and because it is inside
          SidebarInset it spans the content column only, instead of sliding
          under the sidebar the way a fixed element would.

          Desktop only. On a phone the bottom edge belongs to the tab bar, and a
          second strip below it would eat the scarce reachable third of the
          screen; there the credit lives on the login and account screens.
        */}
        {/*
          "A new version is out." Mounted beside the footer rather than inside
          it because the footer is `lg:flex` — desktop only — and the tab that
          sits open across a deploy is overwhelmingly a phone. It is `fixed` and
          renders nothing until a new service worker has installed, so its
          position in this tree costs nothing either way.
        */}
        <UpdatePrompt />

        <footer
          className={cn(
            'sticky bottom-0 z-20 hidden items-center justify-between gap-3 px-4 py-2 lg:flex',
            'border-t border-black/5 dark:border-white/10',
            'bg-background/85 supports-[backdrop-filter]:bg-background/70',
            'text-muted-foreground text-xs backdrop-blur-xl backdrop-saturate-150',
          )}
        >
          {/*
            The signed-in user's own company, never a hard-coded one.

            This said "Livprom d.o.o." while the product was one company's
            internal tool. On a shared installation it credits a stranger's
            company at the foot of every other customer's screen — which is how
            a new customer's first impression becomes "why does this say
            somebody else's name". Same fault as the password mail signing off
            as Livprom, found the same way: by somebody signing in and reading.

            Falls back to the product alone rather than to a placeholder, since
            an owner's own console has no customer to credit.
          */}
          <span>
            KaProLean
            {user?.company?.name && ` · © ${new Date().getFullYear()} ${user.company.name}`}

            {/*
              Which build this is, in the quietest place it can still be read.

              `select-all` so one click takes the whole token — this gets read
              down a phone line or pasted into a message, and a half-selected
              commit is worse than none. Monospace for the same reason: a `0`
              and an `O` have to be tellable apart out loud.

              The title carries the build time as well. It is diagnostic
              information, not an announcement, so the full stamp stays behind a
              hover here and lives in the open only on the account screen, which
              is where support can send somebody on a phone.
            */}
            {' · '}
            <span className="cursor-help font-mono select-all" title={fullVersion()}>
              {shortVersion()}
            </span>
          </span>

          <span>
            Softver:{' '}
            {/* noopener/noreferrer: without them the opened tab gets a handle
                back on this one through window.opener. */}
            <a
              href="https://kala.rs"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground font-medium underline-offset-4 hover:underline"
            >
              Kala Consulting
            </a>{' '}
            &{' '}
            <a
              href="https://yanoka.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground font-medium underline-offset-4 hover:underline"
            >
              Yanoka
            </a>
          </span>
        </footer>
      </SidebarInset>

      {/*
        The two things somebody starts from anywhere in the app, stacked at the
        thumb.

        They used to be one button that changed meaning with the route, which
        was wrong in the way route-dependent controls usually are: a supervisor
        who wanted to file a fault while looking at a 5S sheet had to navigate
        away first to get the button back, and nothing on screen said the button
        had changed. Two buttons say what they are.

        Reporting sits *lowest* because it is the most-used action and the
        bottom of the stack is the easiest place on a phone to reach. The 5S
        button sits above it, and only for people who may actually evaluate —
        offering a walk to somebody the API will refuse is worse than not
        offering it.

        Different colours rather than different icons alone. Two circles of the
        same blue a finger apart read as one control that moved, and the whole
        point of stacking them is that they are not the same thing.
      */}
      <div className="fixed right-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-40 flex flex-col items-center gap-3 lg:bottom-16">
        {user?.canEvaluate && moduleEnabled(modules, 'fiveS') && (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  onClick={() => navigate('/5s/nova')}
                  className="bg-five-s text-five-s-foreground focus-visible:ring-ring/50 flex size-12 items-center justify-center rounded-full shadow-lg transition-transform focus-visible:ring-3 focus-visible:outline-none active:scale-95"
                  // Kept even with the tooltip: a tooltip is not an accessible
                  // name, and it never appears on the phone this mostly lives on.
                  aria-label={t('header.newEvaluation')}
                />
              }
            >
              {/* The same icon the 5S tab carries, so the button and the place
                  it leads are recognisably one thing. */}
              <ClipboardCheck className="size-5" aria-hidden />
            </TooltipTrigger>

            {/* Left, not top. These are pinned to the right edge and stacked,
                so anything above collides with the button above it. */}
            <TooltipContent side="left" sideOffset={10}>
              {t('header.newEvaluation')}
            </TooltipContent>
          </Tooltip>
        )}

        {moduleEnabled(modules, 'submissions') && (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  onClick={() => navigate('/prijave/nova')}
                  className="bg-primary text-primary-foreground focus-visible:ring-ring/50 flex size-14 items-center justify-center rounded-full shadow-lg transition-transform focus-visible:ring-3 focus-visible:outline-none active:scale-95"
                  aria-label={t('header.newSubmission')}
                />
              }
            >
              {/* A megaphone rather than a plus: this raises something — a fault,
                  a hazard, an idea — and "+" said only that a record would be
                  created, which is the least interesting thing about it. */}
              <Megaphone className="size-6" aria-hidden />
            </TooltipTrigger>

            <TooltipContent side="left" sideOffset={10}>
              {t('header.newSubmission')}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/*
        Mobile navigation — an iOS-style floating glass tab bar.

        Three things make it read as a native tab bar rather than a strip of
        links: it floats clear of the screen edges instead of spanning them, the
        background is translucent and blurred so the page shows through, and the
        selection is a pill that *glides* between tabs on navigation rather than
        a colour that snaps.

        `env(safe-area-inset-bottom)` keeps it above the home indicator on a
        notched phone — without it the bar sits under the gesture bar and the
        first tap is swallowed by the OS.
      */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden"
        aria-label={t('nav.mainLabel')}
      >
        <div
          className={cn(
            'relative flex w-full max-w-md items-stretch rounded-[1.75rem] p-1',
            // The glass. saturate lifts the colour that the blur washes out,
            // which is the difference between "frosted" and "grey".
            'bg-card/80 shadow-lg backdrop-blur-2xl backdrop-saturate-150',
            'supports-[backdrop-filter]:bg-card/60',
            // A hairline that catches light on the top edge, like the real thing.
            'border border-black/5 dark:border-white/10',
          )}
        >
          {/*
            The liquid pill.

            Position is computed rather than measured: the tabs are `flex-1`, so
            each is exactly 1/n of the track and the pill only has to translate
            by its own width per index. No refs, no resize observer, and it is
            correct on the first frame instead of after a layout pass.

            The easing overshoots slightly — that small settle at the end is what
            makes it feel like liquid finding its level rather than a box sliding.
          */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-1 left-1 rounded-[1.4rem]',
              'bg-primary/12 ring-primary/25 ring-1',
              'transition-[transform,opacity] duration-500',
              'motion-reduce:transition-none',
              activeIndex < 0 && 'opacity-0',
            )}
            style={{
              width: `calc((100% - 0.5rem) / ${mobileItems.length})`,
              transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`,
              transitionTimingFunction: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
            }}
          >
            {/* Specular highlight — brighter at the top, like glass. */}
            <span className="absolute inset-0 rounded-[1.4rem] bg-gradient-to-b from-white/25 to-transparent dark:from-white/10" />
          </span>

          {mobileItems.map(({ to, labelKey, icon: Icon, hintKey }, index) => {
            const isActive = index === activeIndex

            return (
              <Tooltip key={to}>
                <TooltipTrigger
                  render={
                    /*
                     * A plain className string, not NavLink's `isActive`
                     * callback: Base UI's `render` merges a className *string*
                     * onto the element and would overwrite the function form.
                     * `activeIndex` already knows which tab is current.
                     */
                    <NavLink
                      to={to}
                      end={to === '/'}
                      className={cn(
                        'relative z-10 flex flex-1 flex-col items-center justify-center gap-0.5',
                        'rounded-[1.4rem] py-2 text-[11px] font-medium transition-colors',
                        'focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
                        isActive ? 'text-primary' : 'text-muted-foreground',
                      )}
                    />
                  }
                >
                  <Icon
                    className={cn(
                      'size-5 transition-transform duration-300 motion-reduce:transition-none',
                      isActive && 'scale-110',
                    )}
                    aria-hidden
                  />
                  {t(labelKey)}
                </TooltipTrigger>

                {/* Above, necessarily — the bar is already at the bottom edge. */}
                <TooltipContent side="top" sideOffset={10}>
                  {t(hintKey)}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </nav>
      </SidebarProvider>
    </TooltipProvider>
  )
}
